import { Controller } from "@hotwired/stimulus";

// Provider-agnostic constant for marker z-index (replaces google.maps.Marker.MAX_ZINDEX)
const MARKER_MAX_ZINDEX = 1000000;

// Manages map markers - add, remove, highlight
// Works with map_controller which owns the mapService
export default class MapMarkersController extends Controller {
  static targets = ["embed"];

  get mapService() {
    return this.element._mapService;
  }

  get markers() {
    if (!this.element._markers) {
      this.element._markers = new Map();
    }
    return this.element._markers;
  }

  // Public method - can be called via outlet or event
  addMarkers(eventOrData) {
    if (!this.mapService) return;

    const { markers, replace, fitMarkers } = eventOrData?.detail || eventOrData;

    if (replace) {
      this.clearMarkers();
    }

    markers.forEach(
      ({ lat, lng, label, itemId, url, image, denomination, location }) => {
        if (!lat || !lng) return;
        if (this.markers.has(itemId)) return;
        const marker = this.mapService.addMarker(
          lat,
          lng,
          label,
          this.createMarkerContent(itemId),
          { url, image, denomination, location }, // Callout data
        );
        this.markers.set(itemId, marker);

        marker.addListener("click", () => {
          this.dispatchMarkerEvent("map:marker:click", itemId);
        });

        // Bind mouse events - may need retry for Apple Maps (DOM not immediately available)
        this.bindMarkerMouseEvents(marker, itemId);
      },
    );

    // Store markers on DOM element for persistence
    this.element._markers = this.markers;

    // Fit map to show all markers (e.g., after filter changes)
    if (fitMarkers && this.mapService.showAnnotations) {
      this.mapService.showAnnotations();
    }
  }

  bindMarkerMouseEvents(marker, itemId, retryCount = 0) {
    const content = marker.content;

    if (content) {
      content.addEventListener("mouseenter", () => {
        this.dispatchMarkerEvent("map:marker:mouseover", itemId);
      });
      content.addEventListener("mouseleave", () => {
        this.dispatchMarkerEvent("map:marker:mouseout", itemId);
      });
    } else if (retryCount < 5) {
      // Apple Maps may not have rendered the element yet - retry
      setTimeout(() => {
        this.bindMarkerMouseEvents(marker, itemId, retryCount + 1);
      }, 100);
    }
  }

  clearMarkers() {
    if (!this.mapService) return;

    this.markers.forEach((marker) => {
      this.mapService.removeMarker(marker);
    });
    this.markers.clear();
    this.element._markers = this.markers;
  }

  dispatchMarkerEvent(eventName, itemId) {
    const markerEvent = new CustomEvent(eventName, {
      detail: { itemId },
      bubbles: true,
    });
    // Dispatch on element (not embedTarget) to ensure it always fires
    this.element.dispatchEvent(markerEvent);
  }

  createMarkerContent(label) {
    const div = document.createElement("div");
    div.className = "custom-marker";
    div.textContent = label;
    return div;
  }

  // Public method - can be called via outlet or event
  highlightMarker(eventOrItemId) {
    const itemId = eventOrItemId?.detail?.itemId || eventOrItemId;
    const marker = this.markers.get(itemId);

    if (marker) {
      marker.zIndex = MARKER_MAX_ZINDEX + 1;
      marker.content?.classList?.add("highlight");
      // Apple Maps-specific highlight
      marker.highlight?.();
    }
  }

  // Public method - can be called via outlet or event
  resetMarkerHighlight(eventOrItemId) {
    const itemId = eventOrItemId?.detail?.itemId || eventOrItemId;
    const marker = this.markers.get(itemId);

    if (marker) {
      marker.zIndex = MARKER_MAX_ZINDEX;
      marker.content?.classList?.remove("highlight");
      // Apple Maps-specific unhighlight
      marker.unhighlight?.();
    }
  }
}
